import { logger } from './loggerService'

const DEFAULT_STALL_TIMEOUT_MS = 20000 // 20s with no progress = stalled
const DEFAULT_MAX_RECOVERIES = 5 // give up after N auto-recoveries per upload
const RESUME_DELAY_MS = 500 // gap between pause() and resume()

/**
 * Attach stall auto-recovery to a Firebase resumable upload task.
 *
 * Resumable uploads can hang mid-transfer (background-tab throttling, flaky
 * networks on large files). Pausing then resuming forces Firebase to
 * re-establish the connection from the last committed byte. This watchdog
 * resets a timer on every progress event; if progress stops for too long it
 * auto-recovers, up to `maxRecoveries` times.
 *
 * Decoupled from any store: the caller supplies `isActive()` (true only while
 * the upload should be watched — e.g. not while the user manually paused) and
 * drives `noteProgress()` from its own progress callback.
 *
 * @param {object} uploadTask - Firebase uploadBytesResumable task (needs pause/resume).
 * @param {object} opts
 * @param {() => boolean} opts.isActive - whether to keep watching / allow auto-resume.
 * @param {string} opts.label - log prefix, e.g. '[AdminReviewStore] queue q_3'.
 * @param {number} [opts.stallTimeoutMs]
 * @param {number} [opts.maxRecoveries]
 * @returns {{ noteProgress: (progress?: number) => void, cancel: () => void }}
 *   `noteProgress` is a no-op when the task can't be paused (e.g. a non-resumable
 *   backend), so callers can wire it unconditionally.
 */
export function attachStallRecovery(uploadTask, { isActive, label, stallTimeoutMs = DEFAULT_STALL_TIMEOUT_MS, maxRecoveries = DEFAULT_MAX_RECOVERIES } = {}) {
  // Inert on tasks that don't expose pause/resume (e.g. Bunny/TUS no-op task).
  if (!uploadTask || typeof uploadTask.pause !== 'function' || typeof uploadTask.resume !== 'function') {
    return { noteProgress: () => {}, cancel: () => {} }
  }

  let timer = null
  let recoveryCount = 0
  let lastProgress = 0
  let exhausted = false

  const noteProgress = (progress = lastProgress) => {
    if (exhausted) return
    if (timer) clearTimeout(timer)
    lastProgress = progress

    timer = setTimeout(() => {
      // Only recover if the caller still considers this upload active.
      if (!isActive || !isActive()) return

      recoveryCount++
      if (recoveryCount > maxRecoveries) {
        exhausted = true
        logger.warn(`${label} upload stall recovery exhausted after ${maxRecoveries} attempts`, { lastProgress })
        return
      }

      logger.warn(`${label} upload stall detected, auto-recovering (attempt ${recoveryCount}/${maxRecoveries})`, {
        lastProgress,
        stalledForMs: stallTimeoutMs
      })

      // Pause then resume to force Firebase to re-establish the connection.
      uploadTask.pause()
      setTimeout(() => {
        if (!isActive || !isActive()) return
        uploadTask.resume()
        noteProgress(lastProgress) // re-arm the watchdog
      }, RESUME_DELAY_MS)
    }, stallTimeoutMs)
  }

  const cancel = () => {
    if (timer) clearTimeout(timer)
    timer = null
  }

  noteProgress(0)
  return { noteProgress, cancel }
}
